/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Date Parser - Phase 9 Migration
 * Продвинутый парсинг различных форматов дат
 */


/**
 * Date Parser Class
 * Управляет парсингом дат в различных форматах
 */
class DateParser {
    constructor() {
        this.initialized = false;
        this.supportedFormats = new Map();
        this.parseCache = new Map();
        this.defaultLocale = 'en-US';
        this.userLocale = 'ru-RU';
        
        this.initializeSupportedFormats();
        
        // Manager initialization logging only in debug mode
    }
    
    /**
     * Инициализация поддерживаемых форматов дат
     */
    initializeSupportedFormats() {
        // ISO 8601 formats
        this.supportedFormats.set('iso8601', {
            patterns: [
                /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?(?:Z|[+-]\d{2}:\d{2})$/,
                /^(\d{4})-(\d{2})-(\d{2})$/
            ],
            description: 'ISO 8601 (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)'
        });
        
        // European formats
        this.supportedFormats.set('european', {
            patterns: [
                /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
                /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
                /^(\d{1,2})-(\d{1,2})-(\d{4})$/
            ],
            description: 'European (DD.MM.YYYY, DD/MM/YYYY, DD-MM-YYYY)'
        });
        
        // American formats
        this.supportedFormats.set('american', {
            patterns: [
                /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/,
                /^(\d{1,2})-(\d{1,2})-(\d{4})$/
            ],
            description: 'American (MM/DD/YYYY, MM-DD-YYYY)'
        });
        
        // Russian formats
        this.supportedFormats.set('russian', {
            patterns: [
                /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/,
                /^(\d{1,2})\s+(января|февраля|марта|апреля|мая|июня|июля|августа|сентября|октября|ноября|декабря)\s+(\d{4})$/i,
                /^(\d{1,2})\s+(янв|фев|мар|апр|май|июн|июл|авг|сен|окт|ноя|дек)\.?\s+(\d{4})$/i
            ],
            description: 'Russian (DD.MM.YYYY, DD месяц YYYY)'
        });
        
        // Relative dates
        this.supportedFormats.set('relative', {
            patterns: [
                /^(сегодня|today)$/i,
                /^(вчера|yesterday)$/i,
                /^(завтра|tomorrow)$/i,
                /^(\d+)\s+(дней?|дня|days?|day)\s+(назад|ago)$/i,
                /^(\d+)\s+(недель?|недели|weeks?|week)\s+(назад|ago)$/i,
                /^(\d+)\s+(месяцев?|месяца|months?|month)\s+(назад|ago)$/i
            ],
            description: 'Relative dates (today, yesterday, N days ago)'
        });
        
        // Flexible formats
        this.supportedFormats.set('flexible', {
            patterns: [
                /^(\d{4})(\d{2})(\d{2})$/,  // YYYYMMDD
                /^(\d{2})(\d{2})(\d{4})$/,  // DDMMYYYY
                /^(\d{1,2})[^\d](\d{1,2})[^\d](\d{2,4})$/  // Any separator
            ],
            description: 'Flexible formats (YYYYMMDD, DDMMYYYY, flexible separators)'
        });
    }
    
    /**
     * Основная функция парсинга даты
     * Мигрировано из legacy code
     */
    parseDate(dateString, options = {}) {
        if (!dateString || typeof dateString !== 'string') {
            return null;
        }
        
        const trimmedDate = dateString.trim();
        
        // Check cache first
        if (this.parseCache.has(trimmedDate)) {
            return this.parseCache.get(trimmedDate);
        }
        
        try {
            let parsedDate = null;
            
            // Try each format type
            for (const [formatType, formatData] of this.supportedFormats) {
                parsedDate = this.tryParseWithFormat(trimmedDate, formatType, formatData);
                if (parsedDate) {
                    break;
                }
            }
            
            // Fallback to native Date parsing
            if (!parsedDate) {
                parsedDate = this.tryNativeParsing(trimmedDate);
            }
            
            // Cache successful result
            if (parsedDate) {
                this.parseCache.set(trimmedDate, parsedDate);
                
                // Limit cache size
                if (this.parseCache.size > 1000) {
                    const firstKey = this.parseCache.keys().next().value;
                    this.parseCache.delete(firstKey);
                }
            }
            
            if (window.HyperLog && parsedDate) {
                window.HyperLog.debug('[DateParser] Parsed date:', {
                    input: trimmedDate,
                    output: parsedDate.toISOString(),
                    format: parsedDate._parsedFormat || 'native'
                });
            }
            
            return parsedDate;
            
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[DateParser] Error parsing date:', trimmedDate, error);
            }
            return null;
        }
    }
    
    /**
     * Попытка парсинга с определенным форматом
     */
    tryParseWithFormat(dateString, formatType, formatData) {
        for (const pattern of formatData.patterns) {
            const match = dateString.match(pattern);
            if (match) {
                const result = this.parseByFormatType(match, formatType);
                if (result) {
                    result._parsedFormat = formatType;
                    return result;
                }
            }
        }
        return null;
    }
    
    /**
     * Парсинг по типу формата
     */
    parseByFormatType(match, formatType) {
        try {
            switch (formatType) {
                case 'iso8601':
                    return this.parseISO8601(match);
                case 'european':
                    return this.parseEuropean(match);
                case 'american':
                    return this.parseAmerican(match);
                case 'russian':
                    return this.parseRussian(match);
                case 'relative':
                    return this.parseRelative(match);
                case 'flexible':
                    return this.parseFlexible(match);
                default:
                    return null;
            }
        } catch (error) {
            if (debugMode === '1') {
                console.warn('[DateParser] Format parsing error:', formatType, error);
            }
            return null;
        }
    }
    
    /**
     * Парсинг ISO 8601 формата
     */
    parseISO8601(match) {
        if (match[4]) {
            // Full datetime
            return new Date(`${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${match[6]}`);
        } else {
            // Date only
            return new Date(`${match[1]}-${match[2]}-${match[3]}`);
        }
    }
    
    /**
     * Парсинг европейского формата (DD.MM.YYYY)
     */
    parseEuropean(match) {
        const day = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
        const year = parseInt(match[3], 10);
        
        if (this.isValidDate(day, month + 1, year)) {
            return new Date(year, month, day);
        }
        return null;
    }
    
    /**
     * Парсинг американского формата (MM/DD/YYYY)
     */
    parseAmerican(match) {
        const month = parseInt(match[1], 10) - 1; // Month is 0-indexed
        const day = parseInt(match[2], 10);
        const year = parseInt(match[3], 10);
        
        if (this.isValidDate(day, month + 1, year)) {
            return new Date(year, month, day);
        }
        return null;
    }
    
    /**
     * Парсинг русского формата
     */
    parseRussian(match) {
        if (match[2] && isNaN(match[2])) {
            // Text month format
            const day = parseInt(match[1], 10);
            const monthName = match[2].toLowerCase();
            const year = parseInt(match[3], 10);
            const month = this.getRussianMonthNumber(monthName);
            
            if (month !== -1 && this.isValidDate(day, month, year)) {
                return new Date(year, month - 1, day);
            }
        } else {
            // Numeric format (same as European)
            return this.parseEuropean(match);
        }
        return null;
    }
    
    /**
     * Парсинг относительных дат
     */
    parseRelative(match) {
        const today = new Date();
        const inputLower = match[0].toLowerCase();
        
        if (inputLower.includes('сегодня') || inputLower.includes('today')) {
            return new Date(today.getFullYear(), today.getMonth(), today.getDate());
        }
        
        if (inputLower.includes('вчера') || inputLower.includes('yesterday')) {
            const yesterday = new Date(today);
            yesterday.setDate(today.getDate() - 1);
            return yesterday;
        }
        
        if (inputLower.includes('завтра') || inputLower.includes('tomorrow')) {
            const tomorrow = new Date(today);
            tomorrow.setDate(today.getDate() + 1);
            return tomorrow;
        }
        
        // N days/weeks/months ago
        if (match[1] && (inputLower.includes('назад') || inputLower.includes('ago'))) {
            const number = parseInt(match[1], 10);
            const result = new Date(today);
            
            if (inputLower.includes('день') || inputLower.includes('дня') || inputLower.includes('day')) {
                result.setDate(today.getDate() - number);
            } else if (inputLower.includes('недель') || inputLower.includes('недели') || inputLower.includes('week')) {
                result.setDate(today.getDate() - (number * 7));
            } else if (inputLower.includes('месяц') || inputLower.includes('month')) {
                result.setMonth(today.getMonth() - number);
            }
            
            return result;
        }
        
        return null;
    }
    
    /**
     * Парсинг гибких форматов
     */
    parseFlexible(match) {
        if (match[0].length === 8 && /^\d{8}$/.test(match[0])) {
            // YYYYMMDD
            const year = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
        }
        
        if (match[0].length === 8 && /^\d{8}$/.test(match[0])) {
            // DDMMYYYY
            const day = parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const year = parseInt(match[3], 10);
            return new Date(year, month, day);
        }
        
        // Flexible separator format
        const part1 = parseInt(match[1], 10);
        const part2 = parseInt(match[2], 10);
        const part3 = parseInt(match[3], 10);
        
        // Try different interpretations
        if (part3 > 31) {
            // Third part is year
            if (part1 <= 12 && part2 <= 31) {
                // MM-DD-YYYY
                return new Date(part3, part1 - 1, part2);
            } else if (part2 <= 12 && part1 <= 31) {
                // DD-MM-YYYY
                return new Date(part3, part2 - 1, part1);
            }
        }
        
        return null;
    }
    
    /**
     * Попытка нативного парсинга Date
     */
    tryNativeParsing(dateString) {
        try {
            const nativeDate = new Date(dateString);
            if (!isNaN(nativeDate.getTime())) {
                nativeDate._parsedFormat = 'native';
                return nativeDate;
            }
        } catch (error) {
            // Silent fail
        }
        return null;
    }
    
    /**
     * Получение номера месяца по русскому названию
     */
    getRussianMonthNumber(monthName) {
        const months = {
            'янв': 1, 'января': 1,
            'фев': 2, 'февраля': 2,
            'мар': 3, 'марта': 3,
            'апр': 4, 'апреля': 4,
            'май': 5, 'мая': 5,
            'июн': 6, 'июня': 6,
            'июл': 7, 'июля': 7,
            'авг': 8, 'августа': 8,
            'сен': 9, 'сентября': 9,
            'окт': 10, 'октября': 10,
            'ноя': 11, 'ноября': 11,
            'дек': 12, 'декабря': 12
        };
        
        return months[monthName.toLowerCase()] || -1;
    }
    
    /**
     * Валидация даты
     */
    isValidDate(day, month, year) {
        if (month < 1 || month > 12) return false;
        if (day < 1 || day > 31) return false;
        if (year < 1900 || year > 2100) return false;
        
        // Check for specific month day limits
        const daysInMonth = new Date(year, month, 0).getDate();
        return day <= daysInMonth;
    }
    
    /**
     * Парсинг строки даты с контекстом
     * Мигрировано из legacy parseDateString
     */
    parseDateString(dateString, context = {}) {
        const result = this.parseDate(dateString);
        
        if (result && context) {
            // Add context information
            result._parseContext = {
                originalString: dateString,
                userLocale: context.locale || this.userLocale,
                timezone: context.timezone || 'local',
                ...context
            };
        }
        
        return result;
    }
    
    /**
     * Создание date picker (заглушка для будущего расширения)
     */
    createDatePicker(element, options = {}) {
        if (!element) return null;
        
        try {
            // Basic date picker functionality
            const input = typeof element === 'string' ? document.querySelector(element) : element;
            
            if (!input) return null;
            
            // Set input type to date if supported
            if (input.type !== 'date') {
                input.type = 'date';
            }
            
            // Add event listeners
            input.addEventListener('change', (event) => {
                const dateValue = event.target.value;
                if (dateValue) {
                    const parsedDate = this.parseDate(dateValue);
                    if (options.onChange && typeof options.onChange === 'function') {
                        options.onChange(parsedDate, dateValue);
                    }
                }
            });
            
            if (window.HyperLog) {
                window.HyperLog.debug('[DateParser] Date picker created for element:', element);
            }
            
            return {
                element: input,
                setValue: (date) => {
                    if (date instanceof Date) {
                        input.value = date.toISOString().split('T')[0];
                    }
                },
                getValue: () => {
                    return input.value ? this.parseDate(input.value) : null;
                },
                destroy: () => {
                    // Cleanup if needed
                }
            };
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[DateParser] Error creating date picker:', error);
            }
            return null;
        }
    }
    
    /**
     * Очистка кеша парсинга
     */
    clearCache() {
        this.parseCache.clear();
        if (window.HyperLog) {
            window.HyperLog.debug('[DateParser] Parse cache cleared');
        }
    }
    
    /**
     * Получение поддерживаемых форматов
     */
    getSupportedFormats() {
        const formats = {};
        for (const [key, value] of this.supportedFormats) {
            formats[key] = {
                description: value.description,
                patternCount: value.patterns.length
            };
        }
        return formats;
    }
    
    /**
     * Получение статистики парсера
     */
    getParserStats() {
        return {
            initialized: this.initialized,
            cacheSize: this.parseCache.size,
            supportedFormats: this.supportedFormats.size,
            defaultLocale: this.defaultLocale,
            userLocale: this.userLocale
        };
    }
}

// Создаем singleton instance
const dateParser = new DateParser();
    
// Global exports для backward compatibility
window.dateParser = dateParser;
window.parseDate = dateParser.parseDate.bind(dateParser);
window.parseDateString = dateParser.parseDateString.bind(dateParser);
window.createDatePicker = dateParser.createDatePicker.bind(dateParser);

// ES Module exports
export { dateParser };
export const { parseDate, parseDateString, createDatePicker } = dateParser; 